import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { Input, Button, Tooltip, Space } from 'antd';
import React, { ReactElement, useEffect, Fragment, useState, useRef, useCallback } from 'react';

const { TextArea } = Input;

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  button: {
    textTransform: 'none' as const,
    textAlign: 'left' as const,
    letterSpacing: 'normal',
    justifyContent: 'flex-start',
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    width: '100%',
  },
  flexButtons: {
    display: 'flex',
    flexDirection: 'row' as const,
    justifyContent: 'flex-end',
    marginTop: 4,
  },
  flexRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  hideButtons: {
    display: 'none',
  },
  errorText: {
    fontSize: '0.75rem',
    color: '#d32f2f',
    marginTop: 4,
  },
  textDisplay: {
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  link: {
    textDecoration: 'underline',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};

interface ClickToEditProps {
  allowEmpty?: boolean;
  editMode?: boolean;
  fieldName?: string;
  fieldType?: string;
  fontSize?: number | string;
  height?: number;
  href?: string;
  marginTop?: number;
  multiline?: boolean;
  onTextChange: (value: string) => void;
  resetEditMode?: () => void;
  tableView?: boolean;
  validateItem?: (value: string) => boolean;
  validateText?: string;
  value?: string;
  width?: number | string;
}

function ClickToEdit({
  allowEmpty,
  editMode = false,
  fontSize = 13,
  href = '',
  multiline = false,
  onTextChange,
  resetEditMode,
  validateItem,
  validateText = '',
  value = '',
  width = '100%',
  height,
  marginTop,
  tableView,
  fieldName,
  fieldType = 'text',
}: ClickToEditProps): ReactElement {
  const [isEditMode, setEditMode] = useState(editMode);
  const [textValue, setTextValue] = useState(value);
  const [editedValue, setEditedValue] = useState(value);
  const [isDisabled, setDisabled] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setEditedValue(value);
    setTextValue(value);
  }, [value]);

  const handleResetEditMode = useCallback(() => {
    if (resetEditMode) {
      resetEditMode();
    }
  }, [resetEditMode]);

  const applyAndClose = useCallback(() => {
    if (editedValue?.length || (!editedValue?.length && allowEmpty) || isDirty) {
      setTextValue(editedValue);
      if (!isDisabled && editedValue !== textValue) {
        onTextChange(editedValue);
        setIsDirty(false);
      }
    }

    if (!isDisabled) {
      setEditMode(false);
    }

    handleResetEditMode();
  }, [editedValue, allowEmpty, isDirty, isDisabled, textValue, onTextChange, handleResetEditMode]);

  const revertAndClose = useCallback(() => {
    setTextValue(value);
    setEditedValue(value);
    setEditMode(false);
    setDisabled(false);
    handleResetEditMode();
  }, [value, handleResetEditMode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node) && isEditMode) {
        applyAndClose();
      }
    };

    if (isEditMode) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [isEditMode, applyAndClose]);

  function handleTextChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setEditedValue(e.target.value);
    // TODO: validation refactoring is required
    if ((validateItem !== undefined && validateItem(e.target.value)) || validateItem === undefined) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }
    setIsDirty(true);
  }

  function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    setEditMode(!isEditMode);
  }

  function onKeyPress(evt: React.KeyboardEvent) {
    // Enter is pressed
    if (evt.which === 13) {
      evt.preventDefault();
      evt.stopPropagation();
      applyAndClose();
      // escape key is pressed
    } else if (evt.which === 27) {
      revertAndClose();
    }
  }

  return (
    <div ref={containerRef} style={{ ...styles.container, width: width, minWidth: 150 }}>
      {!isEditMode ? (
        <span className="textWrapper">
          <button
            title={textValue}
            style={{
              ...styles.button,
              ...(height || marginTop ? { height: height, marginTop: marginTop } : {}),
            }}
            onClick={handleClick}
            data-test-id="click-to-edit-button"
          >
            <div
              style={{
                ...styles.textDisplay,
                fontSize: fontSize,
                ...(multiline ? {} : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
              }}
            >
              {href && !tableView ? (
                <div style={styles.flexRow}>
                  <a href={href} target="_blank" rel="noopener noreferrer" style={styles.link}>
                    {textValue}
                  </a>
                  <EditOutlined style={{ marginLeft: 4 }} />
                </div>
              ) : (
                <Fragment>{textValue || ''}</Fragment>
              )}
              {tableView && !textValue && (
                <span style={{ width: '90%', display: 'flex', justifyContent: 'flex-end' }}>
                  <Tooltip title={`Edit ${fieldName}`} placement="bottom">
                    <EditOutlined />
                  </Tooltip>
                </span>
              )}
            </div>
          </button>
        </span>
      ) : (
        <Fragment>
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {multiline ? (
              <TextArea
                autoFocus
                status={isDisabled ? 'error' : ''}
                value={editedValue || ''}
                onChange={handleTextChange}
                onKeyDown={onKeyPress}
                style={{ fontSize: fontSize, lineHeight: 1.5 }}
                data-test-id="click-to-edit-text"
              />
            ) : (
              <Input
                autoFocus
                type={fieldType}
                status={isDisabled ? 'error' : ''}
                value={editedValue || ''}
                onChange={handleTextChange}
                onKeyDown={onKeyPress}
                style={{ fontSize: fontSize, lineHeight: 1.5 }}
                data-test-id="click-to-edit-text"
              />
            )}
            {isDisabled && validateText && <div style={styles.errorText}>{validateText}</div>}
          </div>
          <div style={tableView ? styles.hideButtons : styles.flexButtons}>
            <Space>
              <Button onClick={revertAndClose} icon={<CloseOutlined />} size="small">
                Cancel
              </Button>
              <Button type="primary" onClick={applyAndClose} disabled={isDisabled} icon={<CheckOutlined />} size="small">
                Save
              </Button>
            </Space>
          </div>
        </Fragment>
      )}
    </div>
  );
}

export default ClickToEdit;
